import * as React from "react";
import Progress from "./Progress";
import { createContext, Dispatch, FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import Locations from "./room-bookings/location/Locations";
import Spaces, { SpaceFilters } from "./room-bookings/spaces/spaces/Spaces";
import { UserContext } from "./Auth";
import { DefaultButton, FontSizes, Icon } from "@fluentui/react";
import { Space, Location, useLocationsQuery, useLocationQuery, useFloorsLazyQuery } from "@graphql";
import { isNil } from "lodash";
import LocationLabel from "./shared/location/location-label/LocationLabel";
import Filters, { FilterCache } from "./room-bookings/filters/Filters";
import StatusHeader from "./shared/status-header/StatusHeader";
import { useOutlook } from "@hooks";
import * as Sentry from "@sentry/react";
import { getLocalStorageRegion } from "@shared";
import RegionSelector from "./shared/region-selector/RegionSelector";

export enum Mode {
  LocationSelect,
  SpaceSelect,
  Filters,
  SpaceDetails,
  SelectRegion,
}

export interface Props {
  isOfficeInitialized: boolean;
}

export const FilterCacheContext =
  createContext<[filterCache: FilterCache, setFilterCache: Dispatch<React.SetStateAction<FilterCache>>]>(null);

const AppRoomBookings: FunctionComponent<Props> = (props: Props) => {
  const { isOfficeInitialized } = props;
  const title = "Ricoh Spaces";
  const [location, setLocation] = useState<Location>(null);
  const user = useContext(UserContext);
  const {
    start,
    end,
    outlookTimezone,
    setLocationInEvent,
    removeLocationsFromEvent,
    locationChanges,
    locationChangesUnsubscribe,
  } = useOutlook();
  const [mode, setMode] = useState<Mode>(Mode.SpaceSelect);

  const [spaceAddedToOutlook, setSpaceAddedToOutlook] = useState<Space>(null);
  const [filterCache, setFilterCache] = useState<FilterCache>({ floor: null, spaceType: null, amenities: [] });
  const onSelectedLocationChanged = (location: Location) => {
    setSpaceFilters({ floorId: null, spaceType: null, amenities: null });
    setFilterCache({ floor: null, spaceType: null, amenities: [] });
    setLocation(location);
  };
  const [spaceFilters, setSpaceFilters] = useState<SpaceFilters>({ floorId: null, spaceType: null, amenities: null });
  const { data: allLocations } = useLocationsQuery();
  const { data: userLocation } = useLocationQuery({
    variables: { locationId: user.homeLocation ? user.homeLocation._id : "6225cf331fbe4d07b2f63557" },
  });

  const [getData, { data: floorsData }] = useFloorsLazyQuery();

  useEffect(() => {
    const region = getLocalStorageRegion();
    if (!region) {
      setMode(Mode.SelectRegion);
    }
  }, []);

  useEffect(() => {
    if (!isNil(location)) {
      getData({ variables: { locationId: location._id } });
    }
  }, [location]);

  useEffect(() => {
    if (!isNil(userLocation)) {
      setLocation(userLocation.location);
    }
  }, [userLocation, user]);

  // monitor location changes
  useEffect(() => {
    locationChanges((changes) => {
      // if locations now don't include the selected one, remove it from selection
      setSpaceAddedToOutlook((space) =>
        isNil(space) ||
        !changes.enhancedLocations.some((l) => (l.emailAddress === space.outlookId ? space.outlookId : space.email))
          ? null
          : space,
      );
    });
    return () => {
      // unsubscribe.
      locationChangesUnsubscribe();
    };
  }, [locationChanges, locationChangesUnsubscribe]);

  // puts the space into the outlook event, and removed already added if necessary
  const onAddSpaceToEvent = (space: Space) => {
    if (isNil(spaceAddedToOutlook) && isNil(space)) {
      return;
    }
    if (!isNil(spaceAddedToOutlook) && space._id === spaceAddedToOutlook._id) {
      // trying to add same space to event.
      removeLocationsFromEvent(spaceAddedToOutlook, location, () => {
        setSpaceAddedToOutlook(null);
      });
    } else if (!isNil(spaceAddedToOutlook) && space._id !== spaceAddedToOutlook._id) {
      removeLocationsFromEvent(spaceAddedToOutlook, location, () => {
        setLocationInEvent(space, location);
      });
    } else {
      setLocationInEvent(space, location);
    }
    setSpaceAddedToOutlook(space);
  };

  const onFilterBack = () => {
    setMode(Mode.SpaceSelect);
  };

  const onLocationsBack = () => {
    setMode(Mode.SpaceSelect);
  };

  const onSpacesBack = () => {
    setMode(Mode.SpaceSelect);
  };

  const onFilterChange = (floorId?: string, spaceType?: string, amenities?: string[]) => {
    setSpaceFilters({ floorId, spaceType, amenities });
  };

  const iconChangeRegionClick = () => {
    setMode(Mode.SelectRegion);
  };

  return (
    <>
      {!isOfficeInitialized && (
        <Progress
          title={title}
          logo={require("./../../../assets/logo-filled.png")}
          message="Please side-load your addin to see app body."
        />
      )}
      {mode === Mode.SelectRegion && <RegionSelector />}
      {isOfficeInitialized && isNil(location) && <StatusHeader text={"Loading..."} isLoading={true} />}
      {isOfficeInitialized && !isNil(location) && mode !== Mode.SelectRegion && (
        <>
          {mode !== Mode.LocationSelect && mode !== Mode.SpaceDetails && (
            <div style={{ display: "flex", padding: 10 }}>
              <div style={{ flex: 2 }}>
                <div onClick={() => setMode(Mode.LocationSelect)}>
                  <LocationLabel locationName={location?.name}></LocationLabel>
                </div>
              </div>
              {/* Uncomment the code bellow if you want to have opportunity to change the region
                < div style={{
                  border: '1px solid #E4E4E4', marginRight: 5,
                  cursor: 'pointer', borderRadius: 2,
                  textAlign: "center", width: 30, height: 30
                }}>
                  <Icon iconName="World" onClick={iconChangeRegionClick} style={{
                    fontSize: 16, color: "#AEAEAE",
                    margin: 6
                  }} />
                </div>
                */}
              <div style={{ flex: 1, textAlign: "right" }}>
                <DefaultButton
                  style={{
                    borderColor: "#E4E4E4",
                    borderRadius: 2,
                    color: "#818181",
                  }}
                  iconProps={{ iconName: "Filter" }}
                  text="Filters"
                  onClick={() => setMode(Mode.Filters)}
                  styles={{
                    flexContainer: {
                      flexDirection: "row-reverse",
                    },
                  }}
                />
              </div>
            </div>
          )}
          {mode === Mode.LocationSelect && (
            <Locations
              onChange={onSelectedLocationChanged}
              selectedLocationId={location._id}
              allLocations={allLocations.locations}
              onBackClick={onLocationsBack}
            />
          )}
          {location && (mode === Mode.SpaceSelect || mode === Mode.SpaceDetails) && (
            <Spaces
              onModeChange={(mode) => {
                setMode(mode);
              }}
              start={start}
              end={end}
              locationId={location._id}
              filters={spaceFilters}
              outlookTimezone={outlookTimezone}
              selectedSpaceId={spaceAddedToOutlook?._id}
              floors={floorsData?.floors}
              onAddSpaceToEvent={onAddSpaceToEvent}
              onBackClick={onSpacesBack}
            ></Spaces>
          )}
          {mode === Mode.Filters && (
            <FilterCacheContext.Provider value={[filterCache, setFilterCache]}>
              <Filters
                selectedLocationId={location?._id}
                floors={floorsData?.floors}
                onBackClick={onFilterBack}
                onFilterChange={onFilterChange}
              ></Filters>
            </FilterCacheContext.Provider>
          )}
        </>
      )}
    </>
  );
};
export default AppRoomBookings;
